import { useId } from "react";

import { useAuth } from "../auth/AuthProvider";
import { Link } from "../components/Link";
import { Modal } from "../components/Modal";
import { classPath } from "../router";

import { BasicFlowDiagram } from "./BasicFlowModal";
import { GuideList } from "./GuideList";
import {
  ACCOUNT_MANAGEMENT_ITEMS,
  GALLERY_ITEMS,
  GUIDE_STEPS,
  PRESENTATION_ITEMS,
  STUDENT_STEPS,
} from "./guideSteps";
import { useGuideState } from "./guideStorage";

/** Details of one guide item, opened over the current page. */
export const GuideDetailModal = ({
  title,
  intro,
  items,
  ordered = false,
  onClose,
}: {
  title: string;
  intro?: string;
  items: string[];
  ordered?: boolean;
  onClose: () => void;
}) => (
  <Modal
    title={title}
    hideActions
    primaryLabel="닫기"
    onPrimary={onClose}
    onCancel={onClose}
  >
    {intro && <p>{intro}</p>}
    {items.length > 0 && <GuideList items={items} ordered={ordered} />}
  </Modal>
);

/**
 * The whole guide, shared by the /guide page (`page`) and the dialog. Only the
 * page carries anchors and links to other pages.
 */
export const GuideContent = ({
  userId,
  isTeacher,
  profileClassId,
  page = false,
}: {
  userId: string;
  isTeacher: boolean;
  profileClassId?: string;
  page?: boolean;
}) => {
  const { state, setHidden } = useGuideState(userId);
  const prefix = useId();
  const Heading = page ? "h2" : "h3";

  if (!isTeacher) {
    return (
      <>
        <p className="canvaschool-page-lead">
          캔버스에서 그림을 그리는 방법입니다.{" "}
          <a href="/manual#step-3" target="_blank" rel="noreferrer">
            화면으로 보기
          </a>
        </p>
        <GuideList items={STUDENT_STEPS} ordered />
        {page && profileClassId && (
          <p>
            <Link to={classPath(profileClassId)}>내 학급으로</Link>
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <p className="canvaschool-page-lead">
        학생들은 각자 아이디로 로그인해 선생님이 만든 캔버스에 들어옵니다.
      </p>
      <BasicFlowDiagram />
      <p className="canvaschool-page-lead">
        처음 오셨다면 아래 순서대로 따라 해 보세요.{" "}
        <a href="/manual" target="_blank" rel="noreferrer">
          실제 화면으로 보는 이용 방법
        </a>
      </p>
      {GUIDE_STEPS.map((step, index) => (
        <section
          key={step.id}
          id={page ? step.id : undefined}
          className="canvaschool-guide-section"
          aria-labelledby={`${prefix}-${step.id}`}
        >
          <Heading id={`${prefix}-${step.id}`}>
            {index + 1}. {step.title}
          </Heading>
          <p>{step.description}</p>
          {step.details.length > 0 && <GuideList items={step.details} />}
        </section>
      ))}
      <section
        id={page ? "presentation" : undefined}
        className="canvaschool-guide-section"
        aria-labelledby={`${prefix}-presentation`}
      >
        <Heading id={`${prefix}-presentation`}>발표 수업</Heading>
        <GuideList items={PRESENTATION_ITEMS} />
      </section>
      <section
        id={page ? "gallery" : undefined}
        className="canvaschool-guide-section"
        aria-labelledby={`${prefix}-gallery`}
      >
        <Heading id={`${prefix}-gallery`}>학교 밖 갤러리</Heading>
        <GuideList items={GALLERY_ITEMS} />
      </section>
      <section
        id={page ? "accounts" : undefined}
        className="canvaschool-guide-section"
        aria-labelledby={`${prefix}-accounts`}
      >
        <Heading id={`${prefix}-accounts`}>계정 관리</Heading>
        <GuideList items={ACCOUNT_MANAGEMENT_ITEMS} />
      </section>
      <div className="canvaschool-actions">
        {page && (
          <Link className="canvaschool-btn-secondary" to="/classes">
            학급 목록으로
          </Link>
        )}
        <button
          type="button"
          disabled={!state.hidden}
          aria-describedby={`${prefix}-restore-note`}
          onClick={() => setHidden(false)}
        >
          시작 가이드 다시 보기
        </button>
        {!state.hidden && (
          <span id={`${prefix}-restore-note`} className="canvaschool-hint">
            시작 가이드가 학급 목록과 학급 화면에 보입니다.
          </span>
        )}
      </div>
    </>
  );
};

/** The full guide as a dialog; nothing in it leaves the current page. */
export const GuideModal = ({ onClose }: { onClose: () => void }) => {
  const auth = useAuth();
  const session =
    auth.state.status === "authenticated" ? auth.state.session : null;
  if (!session) {
    return null;
  }
  return (
    <Modal
      title="사용 가이드"
      className="canvaschool-modal--guide"
      hideActions
      primaryLabel="닫기"
      onPrimary={onClose}
      onCancel={onClose}
    >
      <GuideContent
        userId={session.principal.userId}
        isTeacher={session.principal.role === "TEACHER"}
      />
    </Modal>
  );
};
